import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Row } from '@/lib/utils';
import { CaptionLight } from '@/lib/font';
import { colors, semanticColors } from '@/lib/colors';
import WhiteSquareButton from '@/components/Button/WhiteSquareButton';
import ColorSquareButton from '@/components/Button/ColorSquareButton';
import { useLockerCandidates, useAssignLocker } from '@/features/locker/hooks';
import { AssignLockNumberMode, buildAssignLockNumberPayload } from '@/features/locker/lockerDisplay';
import { getMutationErrorMessage } from '@/features/locker/mutationError';
import { handleLockerConflict } from '@/features/facilitySubscription/lockerConflict';
import dayjs from 'dayjs';
import { formatKoreanPhoneNumber } from '../../utils/phoneNumber';

const SEARCH_DEBOUNCE_MS = 350;

interface Props {
    isVisible: boolean;
    onClose: () => void;
    centerId: string;
    zoneId: string;
    lockerNumber: number;
    /** 이 칸에 걸린 고정 비밀번호. 있으면 「고정값 사용 / 이 회원만 다르게」를 묻는다. */
    defaultLockNumber?: string | null;
    createAlert: any;
}

export default function LockerAssignModal({
    isVisible,
    onClose,
    centerId,
    zoneId,
    lockerNumber,
    defaultLockNumber = null,
    createAlert,
}: Props) {
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    /*
     * 예전에는 입력값이 그대로 쿼리키에 들어가 **키 입력 한 글자마다 요청**이 나갔다.
     * 레포 관례대로 useEffect + setTimeout 인라인 디바운스를 건다.
     */
    useEffect(() => {
        const timer = window.setTimeout(() => {
            setSearch(searchInput);
            setPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [searchInput]);

    const { data, isLoading } = useLockerCandidates({
        centerId,
        search,
        page,
        limit: 50,
        enabled: isVisible,
    });

    const assignMutation = useAssignLocker();
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string>('');
    const [lockPassword, setLockPassword] = useState('');
    /** 고정값이 있으면 기본은 상속이다. 없으면 개별 입력 한 칸만 쓰는 기존 화면 그대로다. */
    const [lockMode, setLockMode] = useState<AssignLockNumberMode>(defaultLockNumber ? 'inherit' : 'own');

    const candidates = data?.results || [];
    const totalResults = data?.total || 0;
    const totalPages = Math.ceil(totalResults / 50);

    /*
     * 선택한 후보가 **지금 목록에 있는지**.
     *
     * `useLockerCandidates` 는 page 를 쿼리키에 넣고 keepPreviousData 도 쓰지 않으므로,
     * 페이지를 넘기면 앞 페이지에서 고른 회원은 `candidates` 에 없다. 그 상태로 배정하면
     * **화면에 보이지도 않는 회원**의 이용권이 바뀐다. 프리필도 find 에 실패해 빈 값이 되어
     * 그 회원의 개별 비밀번호가 지워진다.
     */
    const selectedCandidate = candidates.find((item) => item.subscription._id === selectedSubscriptionId) ?? null;

    /** 페이지를 넘기면 선택도 입력도 놓는다. 검색어를 바꿀 때와 같은 규칙이다. */
    const handleChangePage = (nextPage: number) => {
        setPage(nextPage);
        setSelectedSubscriptionId('');
        setLockPassword('');
    };

    const dialogRef = useRef<HTMLDivElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);

    /**
     * 입력값은 **(선택된 후보, 모드)의 함수**로 초기화한다.
     *
     * 이 입력이 뜻하는 것은 "이 회원의 개별 비밀번호"다. 그런데 열 때 비워 두면, 기배정 회원을
     * 고정값 없는 칸으로 옮길 때 **기존값을 유지할 방법이 아예 없다** — 빈 칸이 곧 삭제이기
     * 때문이다. 후보 API 가 raw 개별값(`subscription.lockNumber`)을 그대로 주므로
     * (`lockerZone.service.ts` 의 getAssignCandidates) 그 값을 채워 화면이 실제 저장 상태를
     * 보여주게 한다. 그래야 "빈 칸 = 명시적 삭제"가 참이 된다.
     *
     * **후보가 바뀌면 손으로 고친 값도 버리고 새 후보의 값으로 덮는다.**
     * 이 칸은 회원마다 다른 값이라, 앞 회원에게 입력한 값을 다음 회원에게 끌고 가면 **남의
     * 비밀번호를 그 회원의 개별값으로 저장**하게 된다 — 지금 고치고 있는 바로 그 종류의 사고다.
     * 편집을 잃는 것은 다시 치면 되지만, 잘못 저장된 값은 화면상 구분되지 않는다.
     */
    const prefillFrom = (subscriptionId: string) => {
        const found = candidates.find((item) => item.subscription._id === subscriptionId);
        setLockPassword(found?.subscription.lockNumber ?? '');
    };

    const handleSelectCandidate = (subscriptionId: string) => {
        setSelectedSubscriptionId(subscriptionId);
        // inherit 는 값을 안 쓰므로 채우지 않는다. own 으로 전환할 때 그 시점의 후보로 채운다.
        if (lockMode === 'own') prefillFrom(subscriptionId);
    };

    /**
     * 후보 목록의 roving tabindex + 방향키 선택.
     *
     * radio 는 그룹 전체가 **탭 정지점 하나**여야 하고 방향키로 고르는 것이 표준이다.
     * 후보 50개를 전부 탭 순서에 두면 목록을 빠져나가는 데만 50번을 눌러야 한다.
     * (레포의 GroupTicketPicker 는 role 만 붙어 있고 키보드 처리는 없어서,
     *  실제 로빙 선례인 MemberDetailTabBar 의 moveSelection 을 따랐다.)
     */
    const candidateRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    const moveCandidateSelection = (currentIndex: number, event: React.KeyboardEvent<HTMLButtonElement>) => {
        let targetIndex: number | null = null;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') targetIndex = (currentIndex + 1) % candidates.length;
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft')
            targetIndex = (currentIndex - 1 + candidates.length) % candidates.length;
        if (event.key === 'Home') targetIndex = 0;
        if (event.key === 'End') targetIndex = candidates.length - 1;
        if (targetIndex === null) return;

        event.preventDefault();
        // 방향키로 옮기면 곧바로 선택된다 — radio 그룹의 표준 동작이다.
        const target = candidates[targetIndex];
        handleSelectCandidate(target.subscription._id);
        requestAnimationFrame(() => candidateRefs.current[target.subscription._id]?.focus());
    };

    const handleChangeLockMode = (next: AssignLockNumberMode) => {
        setLockMode(next);
        if (next === 'own') prefillFrom(selectedSubscriptionId);
    };

    // 열리면 검색창으로 포커스를 옮긴다. 그러지 않으면 키보드 사용자는 모달 밖에 남는다.
    useEffect(() => {
        if (!isVisible) return;
        const timer = window.setTimeout(() => searchInputRef.current?.focus(), 0);
        return () => window.clearTimeout(timer);
    }, [isVisible]);

    /**
     * ESC 로 닫고, Tab 을 모달 안에 가둔다.
     *
     * 가두지 않으면 Tab 이 뒤쪽 화면의 요소로 빠져나가 스크린리더 사용자는 자기가 어디에
     * 있는지 알 수 없게 된다.
     */
    const handleDialogKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                onClose();
                return;
            }
            if (event.key !== 'Tab') return;

            const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
                'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
            );
            if (!focusables || focusables.length === 0) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement;

            if (event.shiftKey && active === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        },
        [onClose],
    );

    const handleAssign = (force = false) => {
        // 목록에 없는 선택은 화면이 보여주지 않는 회원이다. 그대로 보내면 안 된다.
        if (!selectedSubscriptionId || !selectedCandidate) {
            return createAlert('알림', '배정할 회원을 선택해주세요.', true);
        }

        assignMutation.mutate(
            {
                centerId,
                subscriptionId: selectedSubscriptionId,
                body: {
                    lockerZoneId: zoneId,
                    lockerNumber: String(lockerNumber),
                    /*
                     * ⚠️ 「칸 고정값 사용」은 `lockNumber: null` **을 명시해 개별값을 비운다.**
                     * 키를 빼면 서버가 미전송을 "변경 없음"으로 읽어(service:1671) 기배정
                     * 이용권의 옛 개별값이 그대로 남는다 — 관리자는 고정값을 골랐는데 옛 값이 뜬다.
                     * 판정과 테스트는 features/locker/lockerDisplay.ts 에 있다.
                     */
                    ...buildAssignLockNumberPayload({ mode: lockMode, ownLockNumber: lockPassword }),
                    ...(force ? { force: true } : {}),
                },
            },
            {
                onSuccess: () => {
                    createAlert('알림', '라커 배정이 완료되었습니다.', false);
                    onClose();
                },
                onError: (error) => {
                    // 라커 중복 경고면 확인 후 강행 배정
                    if (handleLockerConflict(error, createAlert, () => handleAssign(true), { confirmText: '배정하기' })) return;
                    createAlert('오류', getMutationErrorMessage(error, '라커 배정에 실패했습니다.'), true);
                },
            }
        );
    };

    if (!isVisible) return null;

    const titleId = `locker-assign-title-${lockerNumber}`;

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 50,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0.45)',
            }}
            onClick={onClose}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onKeyDown={handleDialogKeyDown}
                style={{
                    backgroundColor: colors.white,
                    borderRadius: 16,
                    width: 520,
                    maxHeight: '85vh',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div
                    style={{
                        padding: '20px 24px',
                        borderBottom: `1px solid ${colors.WHITE_600}`,
                    }}
                >
                    <span id={titleId} style={{ fontSize: 18, fontWeight: 700, color: colors.LAVEL_4 }}>
                        {lockerNumber}번 라커 배정
                    </span>
                    <CaptionLight color={colors.LAVEL_1} style={{ marginTop: 4 }}>
                        라커 이용권을 보유한 회원 목록입니다.
                    </CaptionLight>
                </div>

                {/* Body */}
                <div style={{ padding: 24, flex: 1, overflowY: 'auto' }}>
                    {/* Search */}
                    <div style={{ marginBottom: 16 }}>
                        <input
                            ref={searchInputRef}
                            type="text"
                            aria-label="회원 이름 또는 연락처 검색"
                            placeholder="회원 이름 또는 연락처 검색"
                            style={{
                                width: '100%',
                                padding: '10px 16px',
                                border: `1px solid ${colors.WHITE_600}`,
                                borderRadius: 999,
                                fontSize: 14,
                                outline: 'none',
                                color: colors.LAVEL_4,
                                boxSizing: 'border-box',
                            }}
                            value={searchInput}
                            onChange={(e) => {
                                setSearchInput(e.target.value);
                                // 선택이 풀리면 그 회원의 값도 남기지 않는다. 다음 선택에서 다시 채워진다.
                                setSelectedSubscriptionId('');
                                if (lockMode === 'own') setLockPassword('');
                            }}
                            onFocus={(e) => {
                                (e.target as HTMLInputElement).style.borderColor = colors.PRIMARY_BLUE;
                            }}
                            onBlur={(e) => {
                                (e.target as HTMLInputElement).style.borderColor = colors.WHITE_600;
                            }}
                        />
                    </div>

                    {/* Candidate List */}
                    <Col role="radiogroup" aria-label="배정할 회원" style={{ gap: 8 }}>
                        {isLoading ? (
                            <div
                                style={{
                                    padding: '40px 0',
                                    textAlign: 'center',
                                    fontSize: 13,
                                    color: colors.GREY_200,
                                }}
                            >
                                목록을 불러오는 중...
                            </div>
                        ) : candidates.length === 0 ? (
                            <div
                                style={{
                                    padding: '40px 0',
                                    textAlign: 'center',
                                    fontSize: 13,
                                    color: colors.GREY_200,
                                    backgroundColor: colors.GREY,
                                    borderRadius: 8,
                                }}
                            >
                                라커 이용권을 보유한 회원이 없습니다.
                            </div>
                        ) : (
                            candidates.map((candidate, candidateIndex) => {
                                const isSelected = selectedSubscriptionId === candidate.subscription._id;
                                // 선택이 없으면 첫 항목이 탭 정지점이다. 없으면 목록에 들어갈 수 없다.
                                // 선택이 **현재 목록에** 없으면 첫 항목이 탭 정지점이다.
                                // selectedSubscriptionId 로만 보면 2페이지에서 전부 -1 이 되어 목록에 못 들어간다.
                                const isTabStop = isSelected || (!selectedCandidate && candidateIndex === 0);
                                const isAssigned = candidate.isAssigned;
                                const sub = candidate.subscription;
                                const semantic = isAssigned ? semanticColors.danger : semanticColors.info;
                                const displayLockerNumber = sub.displayLockerNumber || sub.lockerNumber;

                                return (
                                    <button
                                        key={sub._id}
                                        ref={(element) => {
                                            candidateRefs.current[sub._id] = element;
                                        }}
                                        type="button"
                                        role="radio"
                                        aria-checked={isSelected}
                                        aria-label={`${candidate.memberName} ${isAssigned ? '기배정' : '미배정'}`}
                                        tabIndex={isTabStop ? 0 : -1}
                                        onKeyDown={(event) => moveCandidateSelection(candidateIndex, event)}
                                        onClick={() => handleSelectCandidate(sub._id)}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            textAlign: 'left',
                                            padding: '14px 16px',
                                            border: `1px solid ${isSelected ? colors.PRIMARY_BLUE : colors.WHITE_600}`,
                                            borderRadius: 10,
                                            cursor: 'pointer',
                                            backgroundColor: isSelected ? colors.BLUE_50 : isAssigned ? colors.GREY : colors.white,
                                            opacity: isAssigned && !isSelected ? 0.75 : 1,
                                            transition: 'border-color 0.15s, opacity 0.15s',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isSelected) {
                                                (e.currentTarget as HTMLButtonElement).style.borderColor = colors.BLUE_200;
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isSelected) {
                                                (e.currentTarget as HTMLButtonElement).style.borderColor = colors.WHITE_600;
                                            }
                                        }}
                                    >
                                        <Row
                                            style={{
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                marginBottom: 6,
                                            }}
                                        >
                                            <Row style={{ alignItems: 'center', gap: 6 }}>
                                                <span
                                                    style={{
                                                        fontSize: 14,
                                                        fontWeight: 700,
                                                        color: colors.LAVEL_4,
                                                    }}
                                                >
                                                    {candidate.memberName}
                                                </span>
                                                <span style={{ fontSize: 12, color: colors.LAVEL_1 }}>
                                                    {formatKoreanPhoneNumber(candidate.phoneNumber, '연락처 없음')}
                                                </span>
                                            </Row>
                                            <span
                                                style={{
                                                    padding: '2px 8px',
                                                    borderRadius: 4,
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    backgroundColor: semantic.bg,
                                                    color: semantic.text,
                                                    border: `1px solid ${semantic.border}`,
                                                }}
                                            >
                                                {isAssigned ? '기배정' : '미배정'}
                                            </span>
                                        </Row>
                                        <Row
                                            style={{
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <span style={{ fontSize: 12, color: colors.LAVEL_1 }}>
                                                {dayjs(sub.startDate).format('YY.MM.DD')} ~ {dayjs(sub.endDate).format('YY.MM.DD')}
                                            </span>
                                            {isAssigned && <span style={{ fontSize: 11, color: colors.GREY_400 }}>현재 라커: {displayLockerNumber}번</span>}
                                        </Row>
                                    </button>
                                );
                            })
                        )}
                    </Col>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Row
                            style={{
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 12,
                                marginTop: 16,
                            }}
                        >
                            <button
                                disabled={page === 1}
                                onClick={() => handleChangePage(page - 1)}
                                style={{
                                    padding: '4px 12px',
                                    borderRadius: 6,
                                    border: `1px solid ${colors.WHITE_600}`,
                                    backgroundColor: colors.white,
                                    fontSize: 12,
                                    color: colors.LAVEL_1,
                                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                                    opacity: page === 1 ? 0.4 : 1,
                                }}
                            >
                                이전
                            </button>
                            <span style={{ fontSize: 12, color: colors.LAVEL_1 }}>
                                {page} / {totalPages}
                            </span>
                            <button
                                disabled={page >= totalPages}
                                onClick={() => handleChangePage(page + 1)}
                                style={{
                                    padding: '4px 12px',
                                    borderRadius: 6,
                                    border: `1px solid ${colors.WHITE_600}`,
                                    backgroundColor: colors.white,
                                    fontSize: 12,
                                    color: colors.LAVEL_1,
                                    cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                                    opacity: page >= totalPages ? 0.4 : 1,
                                }}
                            >
                                다음
                            </button>
                        </Row>
                    )}

                    {/* 자물쇠 비밀번호 */}
                    <div style={{ marginTop: 20 }}>
                        <span style={{ display: 'block', fontSize: 16, fontWeight: 700, color: colors.LAVEL_4, marginBottom: 8 }}>
                            자물쇠 비밀번호 (선택)
                        </span>

                        {defaultLockNumber ? (
                            /*
                             * 이 칸에 고정 비밀번호가 걸려 있을 때만 묻는다.
                             * 고정값이 없으면 아래의 단일 입력 하나로 기존 화면과 완전히 동일하다.
                             */
                            <div
                                role="radiogroup"
                                aria-label="자물쇠 비밀번호 적용 방식"
                                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                            >
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={lockMode === 'inherit'}
                                    onClick={() => handleChangeLockMode('inherit')}
                                    style={{
                                        textAlign: 'left',
                                        padding: '12px 14px',
                                        borderRadius: 10,
                                        cursor: 'pointer',
                                        backgroundColor: lockMode === 'inherit' ? colors.BLUE_50 : colors.white,
                                        border: `1px solid ${lockMode === 'inherit' ? colors.PRIMARY_BLUE : colors.WHITE_600}`,
                                        boxShadow: lockMode === 'inherit' ? `inset 0 0 0 1px ${colors.PRIMARY_BLUE}` : 'none',
                                    }}
                                >
                                    <span style={{ fontSize: 14, fontWeight: 700, color: colors.LAVEL_4 }}>
                                        이 칸의 고정 비밀번호 사용 ({defaultLockNumber})
                                    </span>
                                    <span style={{ display: 'block', marginTop: 4, fontSize: 12, color: colors.LAVEL_1 }}>
                                        나중에 칸의 고정값을 바꾸면 이 회원에게도 함께 반영됩니다.
                                    </span>
                                </button>

                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={lockMode === 'own'}
                                    onClick={() => handleChangeLockMode('own')}
                                    style={{
                                        textAlign: 'left',
                                        padding: '12px 14px',
                                        borderRadius: 10,
                                        cursor: 'pointer',
                                        backgroundColor: lockMode === 'own' ? colors.BLUE_50 : colors.white,
                                        border: `1px solid ${lockMode === 'own' ? colors.PRIMARY_BLUE : colors.WHITE_600}`,
                                        boxShadow: lockMode === 'own' ? `inset 0 0 0 1px ${colors.PRIMARY_BLUE}` : 'none',
                                    }}
                                >
                                    <span style={{ fontSize: 14, fontWeight: 700, color: colors.LAVEL_4 }}>
                                        이 회원만 다르게 지정
                                    </span>
                                    <span style={{ display: 'block', marginTop: 4, fontSize: 12, color: colors.LAVEL_1 }}>
                                        이 회원에게만 적용됩니다. 칸의 고정값은 그대로 남습니다.
                                    </span>
                                </button>

                                {lockMode === 'own' && (
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        aria-label="이 회원의 자물쇠 비밀번호"
                                        placeholder={`비워 두면 칸의 고정값(${defaultLockNumber})이 적용됩니다.`}
                                        value={lockPassword}
                                        onChange={(e) => setLockPassword(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '10px 16px',
                                            border: `1px solid ${colors.WHITE_600}`,
                                            borderRadius: 999,
                                            fontSize: 14,
                                            outline: 'none',
                                            color: colors.LAVEL_4,
                                            boxSizing: 'border-box',
                                        }}
                                    />
                                )}
                            </div>
                        ) : (
                            <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                aria-label="자물쇠 비밀번호"
                                placeholder="회원이 설정한 자물쇠 번호를 기록해둘 수 있습니다."
                                value={lockPassword}
                                onChange={(e) => setLockPassword(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 16px',
                                    border: `1px solid ${colors.WHITE_600}`,
                                    borderRadius: 999,
                                    fontSize: 14,
                                    outline: 'none',
                                    color: colors.LAVEL_4,
                                    boxSizing: 'border-box',
                                }}
                            />
                        )}
                        {/*
                         * 후보를 고르면 그 회원의 **저장된 개별값**이 채워진다(prefillFrom).
                         * 그래서 빈 칸은 "모름"이 아니라 **"개별 비밀번호 없음"** 이고, 저장하면
                         * 기존 개별값이 지워진다. 프리필이 있어야 이 규약이 참이 된다 —
                         * ⚠️ 프리필을 없애면 「빈 칸 = 삭제」가 대량 소실이 된다.
                         */}
                        <div style={{ fontSize: 11, color: colors.LAVEL_1, marginTop: 6 }}>
                            {lockMode === 'own' && lockPassword.trim() === ''
                                ? '비워 두고 배정하면 이 회원의 개별 비밀번호가 지워집니다.'
                                : '앞자리 0 도 그대로 저장됩니다.'}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div
                    style={{
                        padding: '16px 24px',
                        borderTop: `1px solid ${colors.WHITE_600}`,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 8,
                    }}
                >
                    <WhiteSquareButton text="취소" size="middle" callBack={onClose} />
                    <ColorSquareButton
                        text="배정 완료"
                        size="middle"
                        isDisabled={!selectedCandidate || assignMutation.isLoading}
                        callBack={() => handleAssign()}
                    />
                </div>
            </div>
        </div>
    );
}
